import { getDatabase, ref, get, set, remove, push } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { TodoItem } from '../types/todos';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { recordActivity } from './activityApi';

export class TodosApi {
    private db = getDatabase();

    /**
     * Get todos path based on context (user or building)
     */
    private getTodosPath(contextKey: string, isUserContext: boolean): string {
        return isUserContext
            ? `users/todos/${contextKey}`
            : `buildings/${contextKey}/todos`;
    }

    /**
     * Fetch all todos for a given context
     */
    async getTodos(contextKey: string, isUserContext: boolean): Promise<{ [key: string]: TodoItem }> {
        const todosPath = this.getTodosPath(contextKey, isUserContext);
        const todosRef = ref(this.db, todosPath);
        const snapshot = await get(todosRef);

        if (snapshot.exists()) {
            return snapshot.val();
        }
        return {};
    }

    /**
     * Create a new todo
     */
    async createTodo(contextKey: string, text: string, isUserContext: boolean): Promise<TodoItem> {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad för att skapa en uppgift');
        }

        if (!text.trim()) {
            throw new Error('Text krävs');
        }

        const todosPath = this.getTodosPath(contextKey, isUserContext);
        const todosRef = ref(this.db, todosPath);
        const newTodoRef = push(todosRef);

        const todoItem: TodoItem = {
            key: newTodoRef.key!,
            text: text.trim(),
            done: false,
            createdBy: user.uid,
            createdAt: formatDateForStorage(new Date()),
        };

        await set(newTodoRef, todoItem);
        recordActivity('todo.created', { buildingKey: isUserContext ? undefined : contextKey, targetKey: todoItem.key });
        return todoItem;
    }

    /**
     * Toggle todo done status
     */
    async toggleTodo(
        contextKey: string,
        todoKey: string,
        currentTodo: TodoItem,
        isUserContext: boolean
    ): Promise<void> {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad för att uppdatera en uppgift');
        }

        const todosPath = this.getTodosPath(contextKey, isUserContext);
        const todoRef = ref(this.db, `${todosPath}/${todoKey}`);

        // Create updated todo object without undefined values
        const updatedTodo: TodoItem = {
            key: currentTodo.key,
            text: currentTodo.text,
            done: !currentTodo.done,
            createdBy: currentTodo.createdBy,
            createdAt: currentTodo.createdAt,
        };

        // Only add completion fields if marking as done
        if (!currentTodo.done) {
            updatedTodo.completedBy = user.uid;
            updatedTodo.completedAt = formatDateForStorage(new Date());
        }

        await set(todoRef, updatedTodo);
        recordActivity(currentTodo.done ? 'todo.updated' : 'todo.completed', { buildingKey: isUserContext ? undefined : contextKey, targetKey: todoKey });
    }

    /**
     * Update todo text
     */
    async updateTodo(
        contextKey: string,
        todoKey: string,
        currentTodo: TodoItem,
        newText: string,
        isUserContext: boolean
    ): Promise<void> {
        if (!newText.trim()) {
            throw new Error('Text krävs');
        }

        const todosPath = this.getTodosPath(contextKey, isUserContext);
        const todoRef = ref(this.db, `${todosPath}/${todoKey}`);

        const updatedTodo: TodoItem = {
            ...currentTodo,
            text: newText.trim(),
        };

        await set(todoRef, updatedTodo);
        recordActivity('todo.updated', { buildingKey: isUserContext ? undefined : contextKey, targetKey: todoKey });
    }

    /**
     * Delete a todo
     */
    async deleteTodo(contextKey: string, todoKey: string, isUserContext: boolean): Promise<void> {
        const todosPath = this.getTodosPath(contextKey, isUserContext);
        const todoRef = ref(this.db, `${todosPath}/${todoKey}`);
        await remove(todoRef);
        recordActivity('todo.deleted', { buildingKey: isUserContext ? undefined : contextKey, targetKey: todoKey });
    }
}

// Export singleton instance
export const todosApi = new TodosApi();
